export enum CaseRouteId {
  CASE_OVERVIEW = "CASE_OVERVIEW",
  RECOMMENDATIONS = "RECOMMENDATIONS",
  WORKBENCH = "WORKBENCH",
  ACCOUNT_DETAILS = "ACCOUNT_DETAILS",
  ANALYSIS = "ANALYSIS",
  OBSERVATIONS_RECOMMENDATIONS = "OBSERVATIONS_RECOMMENDATIONS",
  INVESTIGATION_DOCUMENTS = "INVESTIGATION_DOCUMENTS",
  INSPECTIONS_AUDITS = "INSPECTIONS_AUDITS",
  IR = "IR",
  OBSERVATIONS_BY_AUTHORITY = "OBSERVATIONS_BY_AUTHORITY",
  REPORT_PRE_SCN_FIC = "REPORT_PRE_SCN_FIC",
  FRAUD_ELEMENTS = "FRAUD_ELEMENTS",
  REPORT_SCN = "REPORT_SCN",
  REPORT_FIC = "REPORT_FIC",
  REPORT_RO = "REPORT_RO",
  REPORT_FMR = "REPORT_FMR",
  REPORT_LEA = "REPORT_LEA",
  REPORT_BOARD = "REPORT_BOARD",
  REPORT_RECOVERY = "REPORT_RECOVERY",
}

export type WorkbenchChildRouteIds = Extract<CaseRouteId, CaseRouteId.ACCOUNT_DETAILS | CaseRouteId.ANALYSIS | CaseRouteId.OBSERVATIONS_RECOMMENDATIONS | CaseRouteId.INVESTIGATION_DOCUMENTS | CaseRouteId.INSPECTIONS_AUDITS>

const WORKBENCH_CHILD_RELATIVE_PATHS: Record<WorkbenchChildRouteIds, string> = {
  [CaseRouteId.ACCOUNT_DETAILS]: "account-details",
  [CaseRouteId.ANALYSIS]: "analysis",
  [CaseRouteId.OBSERVATIONS_RECOMMENDATIONS]: "observations-recommendations",
  [CaseRouteId.INVESTIGATION_DOCUMENTS]: "documents",
  [CaseRouteId.INSPECTIONS_AUDITS]: "inspections-audits"
}

export const WORKBENCH_RELATIVE_BASE_PATH = "investigation/workbench";

const CASE_ROUTE_RELATIVE_PATHS: Record<CaseRouteId, string> = {
  [CaseRouteId.CASE_OVERVIEW]: "",
  [CaseRouteId.RECOMMENDATIONS]: "recommendations",

  // Investigation Section
  [CaseRouteId.WORKBENCH]: WORKBENCH_RELATIVE_BASE_PATH,
  [CaseRouteId.ACCOUNT_DETAILS]: `${WORKBENCH_RELATIVE_BASE_PATH}/${WORKBENCH_CHILD_RELATIVE_PATHS[CaseRouteId.ACCOUNT_DETAILS]}`,
  [CaseRouteId.ANALYSIS]: `${WORKBENCH_RELATIVE_BASE_PATH}/${WORKBENCH_CHILD_RELATIVE_PATHS[CaseRouteId.ANALYSIS]}`,
  [CaseRouteId.OBSERVATIONS_RECOMMENDATIONS]: `${WORKBENCH_RELATIVE_BASE_PATH}/${WORKBENCH_CHILD_RELATIVE_PATHS[CaseRouteId.OBSERVATIONS_RECOMMENDATIONS]}`,
  [CaseRouteId.INVESTIGATION_DOCUMENTS]: `${WORKBENCH_RELATIVE_BASE_PATH}/${WORKBENCH_CHILD_RELATIVE_PATHS[CaseRouteId.INVESTIGATION_DOCUMENTS]}`,
  [CaseRouteId.INSPECTIONS_AUDITS]: `${WORKBENCH_RELATIVE_BASE_PATH}/${WORKBENCH_CHILD_RELATIVE_PATHS[CaseRouteId.INSPECTIONS_AUDITS]}`,
  [CaseRouteId.IR]: "investigation/ir",
  [CaseRouteId.OBSERVATIONS_BY_AUTHORITY]: "investigation/observations-by-authority",

  // Reports Section
  [CaseRouteId.REPORT_PRE_SCN_FIC]: "reports/pre-scn-fic",
  [CaseRouteId.FRAUD_ELEMENTS]: "reports/fraud-elements",
  [CaseRouteId.REPORT_SCN]: "reports/scn",
  [CaseRouteId.REPORT_FIC]: "reports/fic",
  [CaseRouteId.REPORT_RO]: "reports/ro",
  [CaseRouteId.REPORT_FMR]: "reports/fmr",
  [CaseRouteId.REPORT_LEA]: "reports/lea",
  [CaseRouteId.REPORT_BOARD]: "reports/board-ppt",
  [CaseRouteId.REPORT_RECOVERY]: "reports/recovery",
};

export const getWorkbenchRelativePath = (routeId: WorkbenchChildRouteIds): string => WORKBENCH_CHILD_RELATIVE_PATHS[routeId];

export const getCaseRouteRelativePath = (routeId: CaseRouteId): string => CASE_ROUTE_RELATIVE_PATHS[routeId];

export const getCaseRoutePath = (caseId: string, routeId: CaseRouteId,): string => {
  const relativePath = getCaseRouteRelativePath(routeId);
  const basePath = `/cases/${caseId}`;

  return relativePath ? `${basePath}/${relativePath}` : basePath;
};

export const CASE_ROUTE_IDS = Object.keys(
  CASE_ROUTE_RELATIVE_PATHS,
) as CaseRouteId[];

const ROUTE_IDS_BY_PATH_LENGTH = [...CASE_ROUTE_IDS].sort(
  (a, b) =>
    CASE_ROUTE_RELATIVE_PATHS[b].length - CASE_ROUTE_RELATIVE_PATHS[a].length,
);

const stripQueryAndHash = (path: string): string => path.split(/[?#]/)[0];

export const resolveCaseRouteIdFromPath = (
  pathname: string,
  caseId?: string,
): CaseRouteId | null => {
  if (!caseId) return null;

  const path = stripQueryAndHash(pathname);

  const matchedId = ROUTE_IDS_BY_PATH_LENGTH.find((id) => {
    const routePath = getCaseRoutePath(caseId, id);
    return path === routePath || path.startsWith(`${routePath}/`);
  });

  return matchedId ?? CaseRouteId.CASE_OVERVIEW;
};

export const WORKBENCH_CHILD_NAV_LABELS: Record<WorkbenchChildRouteIds, string> = {
  [CaseRouteId.ACCOUNT_DETAILS]: "Account Details",
  [CaseRouteId.ANALYSIS]: "Fact Finding Analysis",
  [CaseRouteId.OBSERVATIONS_RECOMMENDATIONS]: "Observations & Actions",
  [CaseRouteId.INVESTIGATION_DOCUMENTS]: "Documents",
  [CaseRouteId.INSPECTIONS_AUDITS]: "Inspections & Audits"
}